/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useMemo} from 'react';

import {useAccessToken} from '@salesforce/commerce-sdk-react';
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {configHelper} from '@amblique/base/shared/helpers/config-helper';

/**
 * Service factory
 * Retrieves necessary variables for service instantiation and creates the service instance
 *
 * @template T
 *
 * @param {new() => T} Service
 *
 * @returns {T}
 */
export function useService(Service) {
  const names = Service.variables.sort();
  let app, commerceAPI, locale, origin, shopAPI, site, token;

  if (names.includes('app')) {
    (app = configHelper.getConfig('app'));
  }

  if (names.includes('commerceAPI')) {
    (commerceAPI = configHelper.getConfig('app.commerceAPI.parameters'));
  }

  if (names.includes('locale') || names.includes('site')) {
    ({site, locale} = useMultiSite());
  }

  if (names.includes('origin')) {
    origin = getAppOrigin();
  }

  if (names.includes('shopAPI')) {
    (shopAPI = configHelper.getConfig('app.shopAPI'));
  }

  if (names.includes('token')) {
    ({token} = useAccessToken());
  }

  const variables = useMemo(() => {
    const values = {app, commerceAPI, locale, origin, shopAPI, site, token};

    return Object.keys(values)
      .reduce((variables, name) => {
        if (values[name] !== undefined) {
          variables[name] = values[name];
        }

        return variables;
      }, {})
    ;
  }, [names.join('|'), app, commerceAPI, locale, origin, shopAPI, site, token]);

  return useMemo(() => {
    return Service.variables.every((variable) => !!variables[variable]) && Service.instantiate(variables);
  }, [variables]);
}
